import React from "react";
import { Check, AlertCircle, Clock } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
// import { useToast } from "../../components/ui/use-toast";
import { useNavigate } from "react-router-dom";

interface ImportSummaryProps {
  importName: string;
  totalRecords: number;
  successCount: number;
  failureCount: number;
  duplicateCount: number;
  onFinish: () => void;
  errorMessage?: string;
}

const ImportSummary: React.FC<ImportSummaryProps> = ({
  importName,
  totalRecords,
  successCount,
  failureCount,
  duplicateCount,
  onFinish,
  errorMessage
}) => {
  // const { toast } = useToast();
  const navigate = useNavigate();
  
  const safeTotal = Number.isFinite(totalRecords) && totalRecords > 0 ? totalRecords : 0;
  const safeSuccess = Number.isFinite(successCount) && successCount >= 0 ? successCount : 0;
  const safeFailure = Number.isFinite(failureCount) && failureCount >= 0 ? failureCount : 0;
  const safeDuplicate = Number.isFinite(duplicateCount) && duplicateCount >= 0 ? duplicateCount : 0;
  const successPercentage = safeTotal > 0 ? Math.round((safeSuccess / safeTotal) * 100) : 0;
  
  const handleViewHistory = () => {
    navigate("/dashboard/import-history");
  };

  return (
    <div className="space-y-6">
      {errorMessage && (
        <div className="bg-red-50 p-5 rounded-lg border border-red-200 flex items-center">
          <AlertCircle className="h-6 w-6 text-red-500 mr-3" />
          <div>
            <h3 className="text-lg font-medium text-red-800">Erreur lors de l'import</h3>
            <p className="text-red-700">{errorMessage}</p>
          </div>
        </div>
      )}
      {!errorMessage && (
        <div className="bg-green-50 p-5 rounded-lg border border-green-200 flex items-center">
          <Check className="h-6 w-6 text-green-500 mr-3" />
          <div>
            <h3 className="text-lg font-medium text-green-800">Import terminé avec succès</h3>
            <p className="text-green-700">
              L&apos;import &quot;{importName}&quot; a été traité avec succès.
            </p>
          </div>
        </div>
      )}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
        <h3 className="text-xl font-medium mb-6">Résumé de l&apos;importation</h3>
        
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium">Contacts importés</h4>
              <div className="text-sm text-gray-500 mt-1">
                {safeSuccess} sur {safeTotal} contacts
              </div>
            </div>
            <span className="text-xl font-semibold text-green-600">
              {successPercentage}%
            </span>
          </div>
          <Progress value={successPercentage} className="h-2 bg-gray-100" />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <Check className="h-5 w-5 text-green-500 mr-2" />
                <h5 className="font-medium">Succès</h5>
              </div>
              <div className="text-2xl font-bold">{safeSuccess}</div>
              <div className="text-sm text-gray-500 mt-1">contacts</div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <AlertCircle className="h-5 w-5 text-amber-500 mr-2" />
                <h5 className="font-medium">Doublons</h5>
              </div>
              <div className="text-2xl font-bold">{safeDuplicate}</div>
              <div className="text-sm text-gray-500 mt-1">contacts</div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                <h5 className="font-medium">Erreurs</h5>
              </div>
              <div className="text-2xl font-bold">{safeFailure}</div>
              <div className="text-sm text-gray-500 mt-1">contacts</div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="flex justify-end space-x-4">
        <Button variant="outline" onClick={handleViewHistory}>
          <Clock className="h-4 w-4 mr-2" />
          Historique des imports
        </Button>
        <Button onClick={onFinish}>
          <Check className="h-4 w-4 mr-2" />
          Terminer
        </Button>
      </div>
    </div>
  );
};

export default ImportSummary;
